import { useEffect, useRef, useState } from 'react';
import { get } from '../api/desk';
import type { BoardRow, DeskSnapshot, MarketSummary, PatternAlert } from '../api/generated/types.gen';
import { countdown, price, signed, tenths, toTenths } from '../format';
import { HORIZON_NAMES, sideName, type Horizon, type Place } from '../market';

const left = (close: string | null | undefined, now: number) => close == null ? null : Math.max(0, Math.floor(Date.parse(close) / 1000 - now));

/** Every coin Kalshi lists, each with its live spot, and the horizon toggle with each horizon's own countdown. */
export function MarketBar({ desk, place, now, onPlace, alerts, alertsFor, onAlertsFor }: { desk: DeskSnapshot | null; place: Place; now: number; onPlace: (p: Place) => void; alerts?: PatternAlert[] | null; alertsFor?: string | null; onAlertsFor?: (coin: string | null) => void }) {
  const [board, setBoard] = useState(false);
  const alertsOf = (coin: string) => alerts?.filter(a => a.coin === coin) ?? [];
  const markets = desk?.markets ?? [];
  const current = markets.find(m => m.coin === place.coin);
  const go = (m: MarketSummary) => {
    const has = alertsOf(m.coin).length > 0;
    if (m.coin === place.coin) {
      if (has) { setBoard(false); onAlertsFor?.(alertsFor === m.coin ? null : m.coin); } else setBoard(b => !b);
      return;
    }
    onAlertsFor?.(has ? m.coin : null);
    const listed = m.horizons.find(h => h.horizon === place.horizon);
    // Range buckets stay selected across coins that list them.
    const range = place.family === 'range' && listed?.families?.includes('range');
    onPlace({ coin: m.coin, horizon: listed?.listed ? place.horizon : '15m', ...(range ? { family: 'range' as const } : {}) });
  };
  return <div className="marketbar" data-testid="market-bar">
    <div className="coins" role="group" aria-label="Coins">
      {markets.map(m => {
        const fresh = m.received_at != null && now - m.received_at <= 10;
        const only = m.horizons.filter(h => h.listed).length === 1 ? m.horizons.find(h => h.listed)!.horizon : null;
        // Plan 073: a coin setting up carries a badge, in the colour of its most urgent stage.
        const setting = alertsOf(m.coin);
        const stage = setting.find(a => a.status === 'nearing') ?? setting[0];
        return <button key={m.coin} className={`coin${stage ? ` alerting ${stage.status}` : ''}`} aria-pressed={m.coin === place.coin} aria-expanded={m.coin === place.coin ? board : undefined}
          onClick={() => go(m)} title={setting.length ? `${setting.length} pattern alert${setting.length === 1 ? '' : 's'} on ${m.name}. Click to see ${setting.length === 1 ? 'it' : 'them'}.` : m.coin === place.coin ? 'Show every market' : `Open ${m.name}`} data-coin={m.coin} data-alerts={setting.length || undefined}>
          <b>{m.coin}{m.coin === place.coin && <span className="caret" title="Show every market" data-testid="every-market" onClick={e => { e.stopPropagation(); onAlertsFor?.(null); setBoard(b => !b); }}> ▾</span>}</b>
          <span className={`num${fresh ? '' : ' faint'}`}>{m.spot ? price(m.spot, m.decimals) : '—'}</span>
          {m.change_pct != null && <span className={`num ${Number(m.change_pct) >= 0 ? 'up' : 'down'}`}>{signed(m.change_pct, 1, '%')}</span>}
          {setting.length > 0 && <i className="coin-badge" data-testid="coin-badge">{setting.length}</i>}
          {only && <small className="only">{only === '15m' ? '15m only' : '1h only'}</small>}
        </button>;
      })}
    </div>
    <div className="horizons" role="group" aria-label="Contract horizon">
      {(['hourly', '15m'] as Horizon[]).map(h => {
        const summary = current?.horizons.find(x => x.horizon === h);
        const s = left(summary?.close_time, now);
        return <button key={h} aria-pressed={place.horizon === h} disabled={!!current && !summary?.listed}
          title={summary?.listed === false ? `Kalshi lists no ${HORIZON_NAMES[h]} contracts for ${place.coin}` : undefined}
          onClick={() => onPlace(h === place.horizon ? place : { coin: place.coin, horizon: h })} data-horizon={h}>
          <span>{HORIZON_NAMES[h]}</span><b className="num">{s == null ? '—' : countdown(s)}</b></button>;
      })}
    </div>
    {board && <MarketBoard markets={markets} place={place} onPlace={p => { setBoard(false); onPlace(p); }} onClose={() => setBoard(false)}/>}
  </div>;
}

/** The board of everything: each coin's spot, the hourly strike nearest spot and the live 15-minute window. */
function MarketBoard({ markets, place, onPlace, onClose }: { markets: MarketSummary[]; place: Place; onPlace: (p: Place) => void; onClose: () => void }) {
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const [error, setError] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let dead = false; const ctrl = new AbortController();
    const load = async () => {
      try { const r = await get<BoardRow[]>('/api/v1/markets', ctrl.signal); if (!dead) { setRows(r); setError(''); } }
      catch (e) { if (!dead) setError(e instanceof Error ? e.message : 'Board unavailable'); }
    };
    void load();
    const timer = setInterval(load, 5000);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const away = (e: PointerEvent) => { if (box.current && !box.current.contains(e.target as Node) && !(e.target as Element).closest?.('.coin[aria-expanded]')) onClose(); };
    window.addEventListener('keydown', esc); window.addEventListener('pointerdown', away);
    return () => { dead = true; ctrl.abort(); clearInterval(timer); window.removeEventListener('keydown', esc); window.removeEventListener('pointerdown', away); };
  }, [onClose]);
  const row = (coin: string, h: Horizon, family = h === '15m' ? 'up_down' : 'above_below') =>
    rows?.find(r => r.coin === coin && r.horizon === h && (r.family ?? (r.horizon === '15m' ? 'up_down' : 'above_below')) === family)?.contract ?? null;
  return <div className="board" role="dialog" aria-label="Every market" ref={box} data-testid="market-board">
    <table>
      <thead><tr><th>Coin</th><th>Spot</th><th>1 hour · nearest strike</th><th>1 hour · range at spot</th><th>15 min · this window</th></tr></thead>
      <tbody>{markets.map(m => {
        const hourly = row(m.coin, 'hourly'), bucket = row(m.coin, 'hourly', 'range'), window = row(m.coin, '15m');
        const listed = (h: Horizon) => m.horizons.find(x => x.horizon === h)?.listed;
        const ranged = m.horizons.find(x => x.horizon === 'hourly')?.families?.includes('range');
        return <tr key={m.coin} aria-current={m.coin === place.coin}>
          <td><b>{m.coin}</b> <span className="faint">{m.name}</span></td>
          <td className="num">{m.spot ? price(m.spot, m.decimals) : '—'}</td>
          <td>{!listed('hourly') ? <span className="faint">Not listed</span> : <button onClick={() => onPlace({ coin: m.coin, horizon: 'hourly' })}>
            {hourly ? <><span className="amber">{hourly.label}</span> <span className="up">YES {tenths(toTenths(hourly.yes_ask))}</span></> : rows ? 'No open event' : '…'}</button>}</td>
          <td>{!ranged ? <span className="faint">Not listed</span> : <button onClick={() => onPlace({ coin: m.coin, horizon: 'hourly', family: 'range' })}>
            {bucket ? <><span className="amber">{bucket.label}</span> <span className="up">YES {tenths(toTenths(bucket.yes_ask))}</span></> : rows ? 'No open event' : '…'}</button>}</td>
          <td>{!listed('15m') ? <span className="faint">Not listed</span> : <button onClick={() => onPlace({ coin: m.coin, horizon: '15m' })}>
            {window ? <><span className="amber">{window.label}</span> <span className="up">{sideName('yes', window.ticker)} {tenths(toTenths(window.yes_ask))}</span></> : rows ? 'Next window soon' : '…'}</button>}</td>
        </tr>;
      })}</tbody>
    </table>
    <p className="faint">{error || 'Contracts come from Kalshi’s live catalogue. Prices are market asks, not probabilities.'}</p>
  </div>;
}
